import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useRef } from "react";
import { EASE } from "@/lib/motion";
import { PRESALE_CONFIG, HERO_HIGHLIGHTS, DOC_CONFIG } from "@/config/site";
import NetworkBackground from "./NetworkBackground";
import CountdownTimer from "./CountdownTimer";

const HEADING_LINES = ["XENOVIA", "PRIME"];

// Masked line-by-line reveal for the hero heading.
function MaskLine({ children, delay }) {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function scrollTo(href) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden noise"
      data-testid="hero"
    >
      {/* Layered background */}
      <motion.div style={{ y: reduce ? 0 : bgY }} className="absolute inset-0 -z-10">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <NetworkBackground className="absolute inset-0 h-full w-full" />
        <div className="absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-indigo-600/20 blur-[140px]" />
        <div className="absolute bottom-0 right-1/4 h-[380px] w-[380px] rounded-full bg-violet-600/10 blur-[120px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030305]" />
      </motion.div>

      <motion.div
        style={{ y: reduce ? 0 : contentY, opacity: reduce ? 1 : contentOpacity }}
        className="mx-auto w-full max-w-7xl px-5 pt-28 pb-16 sm:px-8 sm:pt-24"
      >
        {/* Badge */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
          data-testid="hero-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white">
            Presale Starts Nov 1, 2026
          </span>
        </motion.div>

        {/* Heading */}
        <h1 className="mt-8 font-display text-[16vw] font-black uppercase leading-[0.92] tracking-tighter text-white sm:text-[13vw] lg:text-[11rem] lg:leading-[0.86]">
          {HEADING_LINES.map((line, i) => (
            <MaskLine key={line} delay={0.15 + i * 0.12}>
              {i === 1 ? <span className="text-gradient-indigo">{line}</span> : line}
            </MaskLine>
          ))}
        </h1>

        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className="lg:col-span-7"
          >
            <p className="max-w-xl font-display text-lg font-medium uppercase tracking-tight text-white sm:text-xl">
              A Fixed-Supply Digital Asset Built for the Next Generation of Web3
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
              Xenovia Prime is designed as a decentralized utility asset for a growing blockchain
              ecosystem centered around transparency, security, sustainable economics and community
              participation.
            </p>

            {PRESALE_CONFIG.presaleStart && (
              <CountdownTimer
                target={PRESALE_CONFIG.presaleStart}
                className="mt-8 max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl"
              />
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={DOC_CONFIG.whitepaperUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.14em] text-black transition-all hover:bg-indigo-400 hover:text-white"
                data-testid="hero-read-whitepaper"
              >
                Read Whitepaper
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <button
                onClick={() => scrollTo("#about")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all hover:border-white/50 hover:bg-white/5"
                data-testid="hero-explore-xvpr"
              >
                Explore XVPR
              </button>
            </div>
          </motion.div>

          {/* Highlights */}
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
            className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 lg:col-span-5"
          >
            {HERO_HIGHLIGHTS.map((h) => (
              <div key={h.label} className="bg-[#050508]/60 p-5 backdrop-blur-md sm:p-6">
                <div className="font-display text-lg font-bold uppercase tracking-tight text-white sm:text-xl">
                  {h.value}
                </div>
                <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-indigo-300">
                  {h.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#presale")}
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-gray-500 transition-colors hover:text-white lg:flex"
        aria-label="Scroll down"
      >
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        Scroll
      </motion.button>
    </section>
  );
}
